import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { listVideos, bulkAssignAdsToUser } from '../api/videos';
import { getUsers, type AdminUser } from '../services/admin';
import { useAuth } from '../hooks/useAuth';
import { hasAdminPermission, isOwnerAdmin } from '../utils/adminPermissions';

interface SimpleAd {
  _id: string;
  title: string;
  ownerName?: string;
  ownerId?: string;
  createdAt?: string;
}

const isLikelyObjectId = (value: unknown): value is string =>
  typeof value === 'string' && /^[a-f\d]{24}$/i.test(value.trim());

const AdminAdsManagementPage: React.FC = () => {
  const { user } = useAuth();
  const canAccessAdsManagement = isOwnerAdmin(user) || hasAdminPermission(user, 'adAnalytics');

  const [ads, setAds] = useState<SimpleAd[]>([]);
  const [adsLoading, setAdsLoading] = useState<boolean>(true);
  const [adsError, setAdsError] = useState<string | null>(null);

  const [users, setUsers] = useState<AdminUser[]>([]);
  const [usersLoading, setUsersLoading] = useState<boolean>(true);
  const [usersError, setUsersError] = useState<string | null>(null);

  const [selectedAdIds, setSelectedAdIds] = useState<string[]>([]);
  const [targetUserId, setTargetUserId] = useState<string>('');
  const [assigning, setAssigning] = useState<boolean>(false);
  const [assignStatus, setAssignStatus] = useState<string | null>(null);
  const [adsQuery, setAdsQuery] = useState<string>('');

  useEffect(() => {
    let cancelled = false;

    const loadAds = async () => {
      setAdsLoading(true);
      setAdsError(null);
      try {
        const data = await listVideos({
          includeMeta: true,
          pageSize: 50,
          sortBy: 'createdAt',
          sortDir: 'desc',
        });
        if (cancelled) return;

        const payload: unknown = Array.isArray(data)
          ? data
          : Array.isArray((data as { items?: unknown }).items)
            ? (data as { items?: unknown }).items
            : [];

        const typed = Array.isArray(payload)
          ? (payload as {
              _id?: unknown;
              title?: unknown;
              owner?: { id?: unknown; email?: unknown; username?: unknown };
              createdAt?: unknown;
            }[])
          : [];

        const mapped: SimpleAd[] = typed.map((ad) => ({
          _id: String(ad._id ?? ''),
          title: typeof ad.title === 'string' ? ad.title : 'Untitled ad',
          ownerId: ad.owner && ad.owner.id ? String(ad.owner.id) : undefined,
          ownerName:
            typeof ad.owner?.username === 'string'
              ? ad.owner.username
              : typeof ad.owner?.email === 'string'
                ? ad.owner.email
                : undefined,
          createdAt: typeof ad.createdAt === 'string' ? ad.createdAt : undefined,
        })).filter((ad) => isLikelyObjectId(ad._id));

        setAds(mapped);
      } catch {
        if (!cancelled) {
          setAdsError('Failed to load ads');
        }
      } finally {
        if (!cancelled) {
          setAdsLoading(false);
        }
      }
    };

    void loadAds();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadUsers = async () => {
      setUsersLoading(true);
      setUsersError(null);
      try {
        const data = await getUsers();
        if (!cancelled) {
          setUsers(data);
        }
      } catch {
        if (!cancelled) {
          setUsersError('Failed to load users');
        }
      } finally {
        if (!cancelled) {
          setUsersLoading(false);
        }
      }
    };

    void loadUsers();

    return () => {
      cancelled = true;
    };
  }, []);

  const toggleAdSelection = (adId: string) => {
    setSelectedAdIds((prev) =>
      prev.includes(adId) ? prev.filter((id) => id !== adId) : [...prev, adId],
    );
  };

  const handleBulkAssign = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isLikelyObjectId(targetUserId)) {
      setAssignStatus('Choose a user to assign the ads to.');
      return;
    }
    const validSelectedAdIds = selectedAdIds.filter((id) => isLikelyObjectId(id));
    if (!validSelectedAdIds.length) {
      setAssignStatus('Select at least one ad.');
      return;
    }

    setAssigning(true);
    setAssignStatus(null);
    try {
      await bulkAssignAdsToUser({ adIds: validSelectedAdIds, userId: targetUserId });
      setAssignStatus('Assigned ads successfully.');
      setSelectedAdIds([]);
    } catch (err: unknown) {
      const msg =
        err &&
        typeof err === 'object' &&
        'message' in err &&
        typeof (err as { message?: unknown }).message === 'string'
          ? ((err as { message?: unknown }).message as string)
          : 'Failed to assign ads';
      setAssignStatus(msg);
    } finally {
      setAssigning(false);
    }
  };

  const assignSectionHelp = useMemo(() => {
    if (usersLoading) return 'Loading users…';
    if (usersError) return usersError;
    if (!users.length) return 'No users available to assign ads to yet.';
    return 'Choose a user and one or more ads, then assign them in one step.';
  }, [usersLoading, usersError, users.length]);

  const filteredAds = useMemo(() => {
    const q = adsQuery.trim().toLowerCase();
    if (!q) return ads;
    return ads.filter((ad) => {
      const title = ad.title.toLowerCase();
      const owner = ad.ownerName ? ad.ownerName.toLowerCase() : '';
      return title.includes(q) || owner.includes(q);
    });
  }, [ads, adsQuery]);
  
  if (!canAccessAdsManagement) {
    return (
      <main className="mx-auto max-w-2xl px-4 py-10">
        <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 px-4 py-5 text-sm text-slate-900 dark:text-slate-100 shadow-sm">
          <h1 className="text-base font-semibold text-slate-900 dark:text-slate-100">Full admin access required</h1>
          <p className="mt-2 text-xs text-slate-700 dark:text-slate-300">
            Ads management is reserved for full administrators. Your account has limited
            creator-style admin privileges.
          </p>
          <div className="mt-4 flex gap-2 text-xs">
            <Link
              to="/admin"
              className="inline-flex items-center rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-1.5 font-medium text-slate-700 dark:text-slate-300 shadow-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              Back to admin overview
            </Link>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="mx-auto max-w-6xl px-4 py-8 space-y-6">
      <header className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100">Ads management</h1>
          <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
            Review all creatives in the system and bulk-assign them to user profiles.
          </p>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <Link
            to="/admin"
            className="inline-flex items-center rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-1.5 font-medium text-slate-700 dark:text-slate-300 shadow-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            Back to admin overview
          </Link>
        </div>
      </header>

      <section className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4 shadow-sm">
          <div className="mb-3 flex items-center justify-between gap-3">
            <h2 className="text-sm font-semibold text-slate-800 dark:text-slate-200">Ads library</h2>
            <div className="flex flex-1 items-center justify-end gap-2">
              <input
                type="search"
                value={adsQuery}
                onChange={(e) => setAdsQuery(e.target.value)}
                placeholder="Filter by title or owner…"
                className="hidden w-48 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-2 py-1 text-[11px] text-slate-800 dark:text-slate-200 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 sm:block"
              />
              {adsLoading && <span className="text-xs text-slate-500 dark:text-slate-400">Loading ads…</span>}
              {adsError && !adsLoading && (
                <span className="text-xs text-red-600">{adsError}</span>
              )}
            </div>
          </div>
          <div className="max-h-96 overflow-y-auto rounded border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900">
            {ads.length === 0 && !adsLoading && !adsError && (
              <p className="px-3 py-2 text-xs text-slate-500 dark:text-slate-400">No ads found yet.</p>
            )}
            {ads.length > 0 && !adsLoading && filteredAds.length === 0 && (
              <p className="px-3 py-2 text-xs text-slate-500 dark:text-slate-400">
                No ads match your filter. Try a different title or owner.
              </p>
            )}
            {filteredAds.map((ad) => (
              <label
                key={ad._id}
                className="flex cursor-pointer items-center gap-3 border-b border-slate-100 px-3 py-2 text-xs last:border-b-0 hover:bg-white"
              >
                <input
                  type="checkbox"
                  checked={selectedAdIds.includes(ad._id)}
                  onChange={() => toggleAdSelection(ad._id)}
                  className="h-3 w-3 rounded border-slate-300 dark:border-slate-600 text-blue-600 focus:ring-blue-500"
                />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-slate-900 dark:text-slate-100 line-clamp-1">{ad.title}</span>
                    {ad.ownerName && ad.ownerId && (
                      <Link
                        to={`/admin/users/${ad.ownerId}`}
                        className="rounded bg-slate-200 dark:bg-slate-700 px-1.5 py-0.5 text-[10px] font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-300 hover:underline"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {ad.ownerName}
                      </Link>
                    )}
                  </div>
                  {ad.createdAt && (
                    <p className="mt-0.5 text-[11px] text-slate-500 dark:text-slate-400">
                      Created {new Date(ad.createdAt).toLocaleString()}
                    </p>
                  )}
                </div>
                <Link
                  to={`/admin/ads/${ad._id}`}
                  className="ml-2 text-[11px] font-medium text-blue-600 hover:underline"
                  onClick={(e) => e.stopPropagation()}
                >
                  View
                </Link>
              </label>
            ))}
          </div>
          <div className="mt-2 flex items-center justify-between text-[11px] text-slate-600 dark:text-slate-400">
            <span>
              {selectedAdIds.length} ad{selectedAdIds.length === 1 ? '' : 's'} selected
            </span>
            <span className="hidden sm:inline text-slate-500 dark:text-slate-400">
              Showing {filteredAds.length} of {ads.length} ad{ads.length === 1 ? '' : 's'}
            </span>
          </div>
        </div>

        <div className="space-y-4">
          <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4 shadow-sm text-sm">
            <h2 className="text-sm font-semibold text-slate-800 dark:text-slate-200">Bulk assign to user</h2>
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{assignSectionHelp}</p>

            <form onSubmit={handleBulkAssign} className="mt-3 space-y-3 text-xs">
              <div>
                <label className="mb-1 block text-[11px] font-medium text-slate-700 dark:text-slate-300">
                  Target user
                </label>
                <select
                  value={targetUserId}
                  onChange={(e) => setTargetUserId(e.target.value)}
                  disabled={usersLoading || !!usersError || !users.length}
                  className="mt-0.5 block w-full rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-2 py-1 text-xs text-slate-900 dark:text-slate-100 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-slate-100 disabled:text-slate-400"
                >
                  <option value="">Choose a user…</option>
                  {users.filter((u) => isLikelyObjectId(u.id)).map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.username || u.email} ({u.email})
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex items-center justify-between">
                <button
                  type="submit"
                  disabled={assigning || !selectedAdIds.length || !targetUserId}
                  className="inline-flex items-center rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white shadow-sm hover:bg-blue-700 disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
                >
                  {assigning ? 'Assigning…' : 'Assign selected ads'}
                </button>
                <span className="text-[11px] text-slate-500 dark:text-slate-400">
                  This will update the owner of each selected ad.
                </span>
              </div>

              {assignStatus && <p className="text-[11px] text-slate-600 dark:text-slate-400">{assignStatus}</p>}
            </form>
          </div>
        </div>
      </section>
    </main>
  );
};

export default AdminAdsManagementPage;
